import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    BookOpen, ArrowLeft, Save, Loader2, Plus, Trash2, GripVertical,
    ChevronDown, ChevronRight, Clock, Star, StarOff, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEETING_TYPES = [
    { value: 'discovery', label: 'Discovery' },
    { value: 'demo', label: 'Demo' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'closing', label: 'Closing' },
    { value: 'qbr', label: 'QBR' },
    { value: 'custom', label: 'Custom' },
];

function QuestionEditor({ question, onChange, onDelete }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border border-stone-200 rounded-lg p-3 bg-white group">
            <div className="flex items-start gap-2">
                <GripVertical className="w-4 h-4 text-stone-300 mt-1 flex-shrink-0 cursor-grab" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <Input
                            value={question.text || ''}
                            onChange={e => onChange({ ...question, text: e.target.value })}
                            placeholder="Enter question..."
                            className="text-sm flex-1"
                        />
                        <button onClick={() => setExpanded(!expanded)} className="p-1 hover:bg-stone-100 rounded">
                            {expanded ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />}
                        </button>
                        <button onClick={onDelete} className="p-1 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                            <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                    </div>
                    {expanded && (
                        <div className="mt-3 space-y-3 pl-1">
                            <div>
                                <label className="text-[10px] font-medium text-stone-500 uppercase tracking-wide">Purpose</label>
                                <Input
                                    value={question.purpose || ''}
                                    onChange={e => onChange({ ...question, purpose: e.target.value })}
                                    placeholder="Why ask this question?"
                                    className="text-xs mt-1"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-medium text-stone-500 uppercase tracking-wide">Answer Detection Keywords</label>
                                <Input
                                    value={(question.answer_detection_keywords || []).join(', ')}
                                    onChange={e => onChange({ ...question, answer_detection_keywords: e.target.value.split(',').map(k => k.trim()).filter(Boolean) })}
                                    placeholder="budget, timeline, decision maker..."
                                    className="text-xs mt-1"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-medium text-stone-500 uppercase tracking-wide">Follow-up Questions</label>
                                <textarea
                                    value={(question.follow_up_questions || []).join('\n')}
                                    onChange={e => onChange({ ...question, follow_up_questions: e.target.value.split('\n').filter(Boolean) })}
                                    placeholder="One follow-up per line..."
                                    className="w-full text-xs mt-1 border border-stone-200 rounded-lg p-2 resize-none"
                                    rows={3}
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 text-xs">
                                    <input
                                        type="checkbox"
                                        checked={question.required || false}
                                        onChange={e => onChange({ ...question, required: e.target.checked })}
                                        className="rounded"
                                    />
                                    Required
                                </label>
                                <div className="flex items-center gap-1">
                                    <label className="text-[10px] text-stone-500">Product hint:</label>
                                    <Input
                                        value={question.product_category_hint || ''}
                                        onChange={e => onChange({ ...question, product_category_hint: e.target.value })}
                                        placeholder="e.g. CRM"
                                        className="text-xs w-32"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function StageEditor({ stage, onChange, onDelete, onAddQuestion }) {
    const [collapsed, setCollapsed] = useState(false);

    const updateQuestion = (idx, updated) => {
        const questions = [...(stage.questions || [])];
        questions[idx] = updated;
        onChange({ ...stage, questions });
    };

    const removeQuestion = (idx) => {
        const questions = (stage.questions || []).filter((_, i) => i !== idx);
        onChange({ ...stage, questions });
    };

    return (
        <div className="border border-stone-200 rounded-xl bg-stone-50/50 overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-stone-100">
                <GripVertical className="w-4 h-4 text-stone-300 cursor-grab flex-shrink-0" />
                <button onClick={() => setCollapsed(!collapsed)} className="p-0.5">
                    {collapsed ? <ChevronRight className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                </button>
                <Input
                    value={stage.name || ''}
                    onChange={e => onChange({ ...stage, name: e.target.value })}
                    placeholder="Stage name..."
                    className="text-sm font-semibold flex-1 border-none shadow-none p-0 h-auto"
                />
                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-stone-400" />
                        <Input
                            type="number"
                            value={stage.time_budget_minutes || 10}
                            onChange={e => onChange({ ...stage, time_budget_minutes: parseInt(e.target.value) || 10 })}
                            className="w-14 text-xs text-center"
                        />
                        <span className="text-[10px] text-stone-400">min</span>
                    </div>
                    <button onClick={onDelete} className="p-1 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                </div>
            </div>

            {!collapsed && (
                <div className="p-4 space-y-2">
                    {(stage.questions || []).map((q, idx) => (
                        <QuestionEditor
                            key={q.question_id || idx}
                            question={q}
                            onChange={(updated) => updateQuestion(idx, updated)}
                            onDelete={() => removeQuestion(idx)}
                        />
                    ))}
                    <button
                        onClick={onAddQuestion}
                        className="w-full flex items-center justify-center gap-1 py-2 border border-dashed border-stone-300 rounded-lg text-xs text-stone-500 hover:bg-white hover:border-stone-400 transition-colors"
                    >
                        <Plus className="w-3 h-3" /> Add Question
                    </button>
                </div>
            )}
        </div>
    );
}

export default function PlaybookBuilder() {
    const { playbookId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader } = useAuth();
    const [playbook, setPlaybook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);

    useEffect(() => { loadPlaybook(); }, [playbookId]);

    const loadPlaybook = async () => {
        try {
            const res = await axios.get(`${API}/playbooks/${playbookId}`, { headers: getAuthHeader() });
            setPlaybook(res.data);
        } catch (err) {
            toast.error('Failed to load playbook');
            navigate('/playbooks');
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const res = await axios.put(`${API}/playbooks/${playbookId}`, playbook, { headers: getAuthHeader() });
            setPlaybook(res.data);
            toast.success('Playbook saved');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const addStage = () => {
        const stages = [...(playbook.stages || [])];
        stages.push({
            stage_id: crypto.randomUUID().replace(/-/g, '').slice(0, 12),
            name: `Stage ${stages.length + 1}`,
            order: stages.length,
            questions: [],
            time_budget_minutes: 10,
        });
        setPlaybook({ ...playbook, stages });
    };

    const updateStage = (idx, updated) => {
        const stages = [...(playbook.stages || [])];
        stages[idx] = updated;
        setPlaybook({ ...playbook, stages });
    };

    const deleteStage = (idx) => {
        const stages = (playbook.stages || []).filter((_, i) => i !== idx);
        setPlaybook({ ...playbook, stages });
    };

    const addQuestionToStage = (stageIdx) => {
        const stages = [...(playbook.stages || [])];
        const questions = [...(stages[stageIdx].questions || [])];
        questions.push({
            question_id: crypto.randomUUID().replace(/-/g, '').slice(0, 12),
            text: '',
            purpose: '',
            answer_detection_keywords: [],
            follow_up_questions: [],
            required: false,
            product_category_hint: '',
            order: questions.length,
        });
        stages[stageIdx] = { ...stages[stageIdx], questions };
        setPlaybook({ ...playbook, stages });
    };

    const generateWithAI = async () => {
        if (!playbook.name || playbook.name === 'New Playbook') {
            toast.error('Give your playbook a name first');
            return;
        }
        setGenerating(true);
        try {
            const prompt = `Generate a complete sales playbook for a "${playbook.meeting_type}" meeting called "${playbook.name}".
${playbook.description ? `Description: ${playbook.description}` : ''}

Return a JSON object with this exact structure:
{
  "stages": [
    {
      "name": "Stage Name",
      "time_budget_minutes": 10,
      "questions": [
        {
          "text": "The question to ask",
          "purpose": "Why ask this",
          "answer_detection_keywords": ["keyword1", "keyword2"],
          "follow_up_questions": ["Follow-up 1"],
          "required": false,
          "product_category_hint": ""
        }
      ]
    }
  ],
  "description": "A brief description of this playbook"
}

Generate 3-5 stages with 3-6 questions each, appropriate for a ${playbook.meeting_type} sales meeting. Include realistic answer detection keywords and follow-up questions. Return ONLY the JSON, no markdown.`;

            const res = await axios.post(`${API}/ai/chat`, { message: prompt }, { headers: getAuthHeader() });
            const text = res.data?.response || res.data?.message || '';
            const jsonMatch = text.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                const generated = JSON.parse(jsonMatch[0]);
                const stages = (generated.stages || []).map((s, i) => ({
                    stage_id: crypto.randomUUID().replace(/-/g, '').slice(0, 12),
                    name: s.name,
                    order: i,
                    time_budget_minutes: s.time_budget_minutes || 10,
                    questions: (s.questions || []).map((q, j) => ({
                        question_id: crypto.randomUUID().replace(/-/g, '').slice(0, 12),
                        text: q.text,
                        purpose: q.purpose || '',
                        answer_detection_keywords: q.answer_detection_keywords || [],
                        follow_up_questions: q.follow_up_questions || [],
                        required: q.required || false,
                        product_category_hint: q.product_category_hint || '',
                        order: j,
                    })),
                }));
                setPlaybook(prev => ({
                    ...prev,
                    stages,
                    description: generated.description || prev.description,
                }));
                toast.success('Playbook generated with AI!');
            } else {
                toast.error('Could not parse AI response');
            }
        } catch (err) {
            toast.error('AI generation failed');
        } finally {
            setGenerating(false);
        }
    };

    if (loading || !playbook) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    const totalQuestions = (playbook.stages || []).reduce((sum, s) => sum + (s.questions || []).length, 0);
    const totalTime = (playbook.stages || []).reduce((sum, s) => sum + (s.time_budget_minutes || 0), 0);

    return (
        <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/playbooks')} className="p-2 hover:bg-stone-100 rounded-lg">
                        <ArrowLeft className="w-4 h-4 text-stone-600" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-50 border border-blue-200 flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                        <Input
                            value={playbook.name || ''}
                            onChange={e => setPlaybook({ ...playbook, name: e.target.value })}
                            className="text-lg font-bold border-none shadow-none p-0 h-auto"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        />
                        <div className="flex items-center gap-3 text-[11px] text-stone-400 mt-0.5">
                            <span>{(playbook.stages || []).length} stages</span>
                            <span>{totalQuestions} questions</span>
                            <span>{totalTime} min total</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={generateWithAI} disabled={generating} className="gap-1.5">
                        {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        {generating ? 'Generating...' : 'Magic AI'}
                    </Button>
                    <Button onClick={save} disabled={saving} className="gap-2">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save
                    </Button>
                </div>
            </div>

            {/* Settings Row */}
            <div className="bg-white border border-stone-200 rounded-xl p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="text-[10px] font-medium text-stone-500 uppercase tracking-wide">Meeting Type</label>
                        <select
                            value={playbook.meeting_type || 'custom'}
                            onChange={e => setPlaybook({ ...playbook, meeting_type: e.target.value })}
                            className="w-full mt-1 text-sm border border-stone-200 rounded-lg px-3 py-2"
                        >
                            {MEETING_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-medium text-stone-500 uppercase tracking-wide">Status</label>
                        <select
                            value={playbook.status || 'draft'}
                            onChange={e => setPlaybook({ ...playbook, status: e.target.value })}
                            className="w-full mt-1 text-sm border border-stone-200 rounded-lg px-3 py-2"
                        >
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-[10px] font-medium text-stone-500 uppercase tracking-wide">Description</label>
                        <Input
                            value={playbook.description || ''}
                            onChange={e => setPlaybook({ ...playbook, description: e.target.value })}
                            placeholder="Brief description of this playbook..."
                            className="mt-1"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-stone-100">
                    <button
                        onClick={() => setPlaybook({ ...playbook, is_default: !playbook.is_default })}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            playbook.is_default ? 'bg-violet-100 text-violet-700' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                        }`}
                    >
                        {playbook.is_default ? <Star className="w-3.5 h-3.5 fill-current" /> : <StarOff className="w-3.5 h-3.5" />}
                        {playbook.is_default ? 'Default Playbook' : 'Set as Default'}
                    </button>
                </div>
            </div>

            {/* Stages */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-bold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Stages & Questions
                    </h2>
                </div>

                {(playbook.stages || []).map((stage, idx) => (
                    <StageEditor
                        key={stage.stage_id || idx}
                        stage={stage}
                        onChange={(updated) => updateStage(idx, updated)}
                        onDelete={() => deleteStage(idx)}
                        onAddQuestion={() => addQuestionToStage(idx)}
                    />
                ))}

                <button
                    onClick={addStage}
                    className="w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-stone-300 rounded-xl text-sm text-stone-500 hover:bg-stone-50 hover:border-stone-400 transition-colors"
                >
                    <Plus className="w-4 h-4" /> Add Stage
                </button>
            </div>
        </div>
    );
}
